import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Info, Plus, Trash2, Workflow } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import CampaignTagSelector from "@/components/campaigns/CampaignTagSelector";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

interface StepDraft {
  id: string;
  value: number;
  unit: "minutes" | "hours" | "days";
  /** "t:<templateId>" | "f:<fluxoId>" | "ia" (legado, apenas leitura) */
  action: string;
}

interface Option {
  id: string;
  name: string;
}

interface StageOption {
  id: string;
  name: string;
  pipelineName: string;
}

const UNIT_OPTIONS = [
  { value: "minutes", label: "Minutos" },
  { value: "hours", label: "Horas" },
  { value: "days", label: "Dias" },
];

const DAYS_OF_WEEK = [
  { value: "seg", label: "Seg" },
  { value: "ter", label: "Ter" },
  { value: "qua", label: "Qua" },
  { value: "qui", label: "Qui" },
  { value: "sex", label: "Sex" },
  { value: "sab", label: "Sáb" },
  { value: "dom", label: "Dom" },
];

const inputClass =
  "h-11 !h-[44px] rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] focus-visible:ring-[#2563EB]/20 dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA] !w-full text-[14px]";

const selectTriggerClass =
  "!h-[44px] w-full rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA]";

const FollowUpEditor: React.FC = () => {
  const navigate = useNavigate();
  const { id: followUpId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isEditing = Boolean(followUpId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const [assistants, setAssistants] = useState<Option[]>([]);
  const [templates, setTemplates] = useState<(Option & { content?: string })[]>([]);
  const [fluxos, setFluxos] = useState<Option[]>([]);
  const [stages, setStages] = useState<StageOption[]>([]);

  const [name, setName] = useState("");
  const [assistantId, setAssistantId] = useState("");
  const [goalStageId, setGoalStageId] = useState("none");
  const [goalTags, setGoalTags] = useState<string[]>([]);
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("18:00");
  const [selectedDays, setSelectedDays] = useState<string[]>(["seg", "ter", "qua", "qui", "sex"]);
  const [steps, setSteps] = useState<StepDraft[]>([
    { id: crypto.randomUUID(), value: 30, unit: "minutes", action: "" },
  ]);
  const [stopOnHuman, setStopOnHuman] = useState(true);
  const [stopOnWonLost, setStopOnWonLost] = useState(true);
  const [exclusionTags, setExclusionTags] = useState<string[]>([]);
  const [maxCycles, setMaxCycles] = useState(1);
  const [cooldownDays, setCooldownDays] = useState(30);
  const [isActive, setIsActive] = useState(true);
  const [activeFrom, setActiveFrom] = useState<string | null>(null);

  const showMessage = (type: "success" | "error", text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const loadOptions = useCallback(async () => {
    if (!user?.account_id || !supabase) return;
    const [assistantsRes, templatesRes, fluxosRes, stagesRes] = await Promise.all([
      supabase
        .from("account_assistants")
        .select("id, name")
        .eq("account_id", user.account_id)
        .order("name"),
      supabase
        .from("account_templates")
        .select("id, name, content")
        .eq("account_id", user.account_id)
        .eq("is_active", true)
        .order("name"),
      supabase
        .from("account_fluxos")
        .select("id, name")
        .eq("account_id", user.account_id)
        .order("name"),
      supabase
        .from("account_pipeline_stages")
        .select("id, name, pipeline:account_pipelines(id, name)")
        .eq("account_id", user.account_id)
        .order("order_index"),
    ]);

    setAssistants((assistantsRes.data || []) as Option[]);
    setTemplates((templatesRes.data || []) as (Option & { content?: string })[]);
    setFluxos((fluxosRes.data || []) as Option[]);
    type StageWithPipeline = { id: string; name: string; pipeline: { id: string; name: string } | null };
    setStages(
      ((stagesRes.data || []) as unknown as StageWithPipeline[]).map((stage) => ({
        id: stage.id,
        name: stage.name,
        pipelineName: stage.pipeline?.name || "Funil",
      }))
    );
  }, [user?.account_id]);

  const loadFollowUp = useCallback(async () => {
    if (!followUpId || !supabase) return;
    const [{ data: followUp, error }, { data: intervals, error: intervalsError }] = await Promise.all([
      supabase.from("account_assistant_follow_ups").select("*").eq("id", followUpId).maybeSingle(),
      supabase
        .from("account_assistant_follow_up_intervals")
        .select("*")
        .eq("follow_up_id", followUpId)
        .order("sequence_order", { ascending: true }),
    ]);

    if (error || !followUp) throw error || new Error("Follow-up não encontrado");
    if (intervalsError) throw intervalsError;

    setName(followUp.name);
    setAssistantId(followUp.assistant_id);
    setGoalStageId(followUp.goal_pipeline_stage_id || "none");
    setGoalTags(followUp.goal_tags || []);
    setStartTime(String(followUp.window_start).slice(0, 5));
    setEndTime(String(followUp.window_end).slice(0, 5));
    setSelectedDays(followUp.days_of_week || []);
    setStopOnHuman(followUp.stop_on_human ?? true);
    setStopOnWonLost(followUp.stop_on_won_lost ?? true);
    setExclusionTags(followUp.exclusion_tags || []);
    setMaxCycles(followUp.max_cycles ?? 1);
    setCooldownDays(followUp.cooldown_days ?? 30);
    setIsActive(followUp.is_active);
    setActiveFrom(followUp.active_from ?? null);
    type IntervalRow = {
      id: string;
      delay_value: number;
      delay_unit: "minutes" | "hours" | "days";
      template_id: string | null;
      fluxo_id: string | null;
    };
    setSteps(
      ((intervals || []) as IntervalRow[]).map((interval) => ({
        id: interval.id,
        value: interval.delay_value,
        unit: interval.delay_unit,
        action: interval.fluxo_id
          ? `f:${interval.fluxo_id}`
          : interval.template_id
            ? `t:${interval.template_id}`
            : "ia",
      }))
    );
  }, [followUpId]);

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      try {
        await loadOptions();
        if (isEditing) await loadFollowUp();
      } catch (error) {
        console.error("Erro ao carregar dados do follow-up:", error);
        showMessage("error", "Erro ao carregar dados do follow-up");
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, [loadOptions, loadFollowUp, isEditing]);

  const activeFromLabel = useMemo(() => {
    if (!activeFrom) return null;
    const parsed = new Date(activeFrom);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
  }, [activeFrom]);

  const stepPreview = useMemo(() => {
    return (step: StepDraft): string => {
      if (step.action.startsWith("t:")) {
        const template = templates.find((item) => item.id === step.action.slice(2));
        return template?.content || "Template selecionado";
      }
      if (step.action.startsWith("f:")) {
        const fluxo = fluxos.find((item) => item.id === step.action.slice(2));
        return fluxo ? `Dispara o fluxo "${fluxo.name}" (blocos de texto, imagem, áudio e delay).` : "Fluxo selecionado";
      }
      if (step.action === "ia") return "Mensagem gerada com IA (configuração antiga — troque por texto ou fluxo).";
      return "Escolha o que enviar nesta etapa.";
    };
  }, [templates, fluxos]);

  const addStep = () =>
    setSteps((prev) => [...prev, { id: crypto.randomUUID(), value: 1, unit: "days", action: "" }]);

  const removeStep = (id: string) => {
    setSteps((prev) => (prev.length > 1 ? prev.filter((step) => step.id !== id) : prev));
  };

  const updateStep = (id: string, patch: Partial<StepDraft>) =>
    setSteps((prev) => prev.map((step) => (step.id === id ? { ...step, ...patch } : step)));

  const toggleDay = (day: string) =>
    setSelectedDays((prev) => (prev.includes(day) ? prev.filter((item) => item !== day) : [...prev, day]));

  const validate = (): boolean => {
    if (!name.trim()) {
      showMessage("error", "Informe o nome da sequência");
      return false;
    }
    if (!assistantId) {
      showMessage("error", "Selecione o assistente responsável");
      return false;
    }
    if (selectedDays.length === 0) {
      showMessage("error", "Selecione pelo menos um dia da semana");
      return false;
    }
    if (startTime >= endTime) {
      showMessage("error", "O horário inicial deve ser menor que o final");
      return false;
    }
    if (steps.some((step) => !step.action)) {
      showMessage("error", "Toda etapa precisa de um texto ou fluxo selecionado");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!user?.account_id || !supabase) return;
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        account_id: user.account_id,
        assistant_id: assistantId,
        name: name.trim(),
        window_start: startTime,
        window_end: endTime,
        days_of_week: selectedDays,
        is_active: isActive,
        goal_pipeline_stage_id: goalStageId === "none" ? null : goalStageId,
        goal_tags: goalTags,
        stop_on_human: stopOnHuman,
        stop_on_won_lost: stopOnWonLost,
        exclusion_tags: exclusionTags,
        max_cycles: Math.max(1, maxCycles),
        cooldown_days: Math.max(0, cooldownDays),
      };

      let savedId = followUpId;
      if (isEditing && followUpId) {
        const { error } = await supabase
          .from("account_assistant_follow_ups")
          .update(payload)
          .eq("id", followUpId);
        if (error) throw error;

        const { error: deleteError } = await supabase
          .from("account_assistant_follow_up_intervals")
          .delete()
          .eq("follow_up_id", followUpId);
        if (deleteError) throw deleteError;
      } else {
        const { data: inserted, error } = await supabase
          .from("account_assistant_follow_ups")
          .insert(payload)
          .select("id")
          .single();
        if (error) throw error;
        savedId = inserted.id;
      }

      const intervalsToInsert = steps.map((step, index) => ({
        follow_up_id: savedId!,
        delay_value: step.value,
        delay_unit: step.unit,
        template_id: step.action.startsWith("t:") ? step.action.slice(2) : null,
        fluxo_id: step.action.startsWith("f:") ? step.action.slice(2) : null,
        sequence_order: index,
      }));

      const { error: intervalsError } = await supabase
        .from("account_assistant_follow_up_intervals")
        .insert(intervalsToInsert);
      if (intervalsError) throw intervalsError;

      navigate("/follow-ups");
    } catch (error) {
      console.error("Erro ao salvar follow-up:", error);
      showMessage("error", "Erro ao salvar a sequência de follow-up");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full h-[calc(100dvh-110px)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-6 h-6 border-3 border-border border-t-primary rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-6 pb-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/follow-ups")}
            className="w-[38px] h-[38px] flex items-center justify-center rounded-[10px] border border-border bg-card hover:bg-[#F1F5F9] transition-colors dark:hover:bg-[#27272A]"
          >
            <ArrowLeft size={18} className="text-[#64748B] dark:text-neutral-400" />
          </button>
          <h1 className="text-[28px] sm:text-[32px] leading-tight font-bold text-foreground tracking-tight">
            {isEditing ? "Editar sequência" : "Nova sequência"}
          </h1>
        </div>
        <Button onClick={handleSave} disabled={saving} className="min-h-[40px] px-5">
          {saving ? "Salvando..." : "Salvar sequência"}
        </Button>
      </div>

      {message && (
        <div
          className={`p-3 rounded-md ${
            message.type === "success"
              ? "bg-[#ECFDF5] border border-green-200 text-[#16A34A] dark:bg-green-500/10 dark:border-green-500/30 dark:text-[#22C55E]"
              : "bg-[#FEF2F2] border border-red-200 text-[#DC2626] dark:bg-red-500/10 dark:border-red-500/30 dark:text-[#F87171]"
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-5 items-start">
        {/* Coluna esquerda: regras */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 p-5 rounded-xl border border-[#E5E7EB] bg-white shadow-sm dark:border-[#3F3F464D] dark:bg-[#27272A] dark:shadow-none">
            <h3 className="text-base font-semibold text-[#0F172A] dark:text-[#FAFAFA]">Configuração</h3>

            <div className="flex flex-col gap-2">
              <Label htmlFor="fu-name">Nome da sequência</Label>
              <Input
                id="fu-name"
                placeholder="Ex: Reengajar orçamento parado"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputClass}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label>Assistente responsável</Label>
              <Select value={assistantId || undefined} onValueChange={setAssistantId}>
                <SelectTrigger className={selectTriggerClass}>
                  <SelectValue placeholder="Selecione o assistente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {assistants.map((assistant) => (
                      <SelectItem key={assistant.id} value={assistant.id}>
                        {assistant.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <Checkbox id="fu-active" checked={isActive} onCheckedChange={(v) => setIsActive(v === true)} />
                <Label htmlFor="fu-active" className="cursor-pointer">Sequência ativa</Label>
              </div>
              <p className="text-[11px] text-[#94A3B8] dark:text-[#71717A]">
                {activeFromLabel
                  ? `Vale apenas para conversas a partir de ${activeFromLabel}. O que aconteceu antes fica de fora, e reativar a sequência move esse corte para o momento da reativação.`
                  : "Vale apenas para conversas a partir do momento da ativação. O histórico anterior fica de fora, então ativar nunca dispara follow-up retroativo."}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-4 p-5 rounded-xl border border-[#E5E7EB] bg-white shadow-sm dark:border-[#3F3F464D] dark:bg-[#27272A] dark:shadow-none">
            <div>
              <div className="flex items-center gap-1.5">
                <h3 className="text-base font-semibold text-[#0F172A] dark:text-[#FAFAFA]">Objetivo</h3>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      aria-label="Como funciona o objetivo"
                      className="flex items-center justify-center w-5 h-5 rounded-full text-[#94A3B8] hover:text-[#2563EB] hover:bg-[#EEF4FF] transition-colors dark:text-[#71717A] dark:hover:text-[#60A5FA] dark:hover:bg-blue-500/15"
                    >
                      <Info size={14} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="!bg-white border border-[#E5E7EB] p-4 w-[300px] rounded-xl shadow-md dark:!bg-[#27272A] dark:border-[#3F3F46] dark:shadow-none"
                  >
                    <p className="text-sm font-semibold text-[#0F172A] dark:text-[#FAFAFA] mb-2">
                      Como funciona o objetivo
                    </p>
                    <p className="text-xs text-[#475569] dark:text-[#A1A1AA] leading-relaxed">
                      Você pode definir uma etapa do funil, tags de meta, ou os dois. Basta o
                      contato atingir{" "}
                      <strong className="font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
                        um deles
                      </strong>{" "}
                      — o que acontecer primeiro — para a cadência encerrar com sucesso e contar
                      como conversão.
                    </p>
                    <p className="text-xs text-[#475569] dark:text-[#A1A1AA] leading-relaxed mt-2">
                      Sem objetivo definido, a cadência roda até o fim das etapas e ciclos, ou até
                      uma regra de parada.
                    </p>
                  </PopoverContent>
                </Popover>
              </div>
              <p className="text-xs text-[#64748B] dark:text-[#A1A1AA] mt-1">
                A cadência encerra com sucesso quando o contato atingir o objetivo.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Label>Etapa do funil</Label>
              <Select value={goalStageId} onValueChange={setGoalStageId}>
                <SelectTrigger className={selectTriggerClass}>
                  <SelectValue placeholder="Sem meta de funil" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="none">Sem meta de funil</SelectItem>
                    {stages.map((stage) => (
                      <SelectItem key={stage.id} value={stage.id}>
                        {stage.pipelineName} · {stage.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2 w-full">
              <CampaignTagSelector
                label="Tags de meta"
                selectedTags={goalTags}
                onTagsChange={setGoalTags}
              />
              <p className="text-[11px] text-[#94A3B8] dark:text-[#71717A]">
                Quando o contato receber qualquer uma destas tags, a cadência encerra com sucesso.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-4 p-5 rounded-xl border border-[#E5E7EB] bg-white shadow-sm dark:border-[#3F3F464D] dark:bg-[#27272A] dark:shadow-none">
            <h3 className="text-base font-semibold text-[#0F172A] dark:text-[#FAFAFA]">Janela de envio</h3>
            <div className="flex items-center gap-3">
              <div className="flex flex-col gap-1 flex-1">
                <span className="text-xs text-[#64748B] dark:text-[#71717A]">De</span>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="bg-white text-[#0F172A] text-[14px] h-[44px] px-3 rounded-xl border border-[#E5E7EB] outline-none focus-visible:border-[#2563EB] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:border-[#3F3F46] dark:[&::-webkit-calendar-picker-indicator]:invert"
                />
              </div>
              <div className="flex flex-col gap-1 flex-1">
                <span className="text-xs text-[#64748B] dark:text-[#71717A]">Até</span>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="bg-white text-[#0F172A] text-[14px] h-[44px] px-3 rounded-xl border border-[#E5E7EB] outline-none focus-visible:border-[#2563EB] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:border-[#3F3F46] dark:[&::-webkit-calendar-picker-indicator]:invert"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {DAYS_OF_WEEK.map((day) => (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => toggleDay(day.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    selectedDays.includes(day.value)
                      ? "bg-[#2563EB] border-[#2563EB] text-white dark:bg-blue-500/30 dark:border-blue-500/40 dark:text-[#93C5FD]"
                      : "bg-white border-[#E5E7EB] text-[#64748B] hover:border-[#2563EB]/40 dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#A1A1AA]"
                  }`}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-4 p-5 rounded-xl border border-[#E5E7EB] bg-white shadow-sm dark:border-[#3F3F464D] dark:bg-[#27272A] dark:shadow-none">
            <div className="flex items-center gap-1.5">
              <h3 className="text-base font-semibold text-[#0F172A] dark:text-[#FAFAFA]">Paradas automáticas</h3>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    aria-label="Como funcionam as paradas automáticas"
                    className="flex items-center justify-center w-5 h-5 rounded-full text-[#94A3B8] hover:text-[#2563EB] hover:bg-[#EEF4FF] transition-colors dark:text-[#71717A] dark:hover:text-[#60A5FA] dark:hover:bg-blue-500/15"
                  >
                    <Info size={14} />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="!bg-white border border-[#E5E7EB] p-4 w-[300px] rounded-xl shadow-md dark:!bg-[#27272A] dark:border-[#3F3F46] dark:shadow-none"
                >
                  <p className="text-sm font-semibold text-[#0F172A] dark:text-[#FAFAFA] mb-2">
                    Pausar × Encerrar
                  </p>
                  <p className="text-xs text-[#475569] dark:text-[#A1A1AA] leading-relaxed">
                    <strong className="font-semibold text-[#0F172A] dark:text-[#FAFAFA]">Pausar</strong>{" "}
                    é temporário e retoma sozinho: quando um atendente assume, a cadência volta
                    assim que a IA responder de novo; com tag de exclusão, volta quando a tag for
                    removida.
                  </p>
                  <p className="text-xs text-[#475569] dark:text-[#A1A1AA] leading-relaxed mt-2">
                    <strong className="font-semibold text-[#0F172A] dark:text-[#FAFAFA]">Encerrar</strong>{" "}
                    é definitivo: lead ganho/perdido, cliente pedindo para parar (opt-out) ou o
                    limite de ciclos esgotado. Dá para reativar manualmente pelo Monitor.
                  </p>
                </PopoverContent>
              </Popover>
            </div>

            <label className="flex items-start gap-2.5 cursor-pointer">
              <Checkbox checked={stopOnHuman} onCheckedChange={(v) => setStopOnHuman(v === true)} className="mt-0.5" />
              <span className="text-sm text-[#0F172A] dark:text-[#FAFAFA]">
                Pausar quando um atendente assumir a conversa
              </span>
            </label>

            <label className="flex items-start gap-2.5 cursor-pointer">
              <Checkbox checked={stopOnWonLost} onCheckedChange={(v) => setStopOnWonLost(v === true)} className="mt-0.5" />
              <span className="text-sm text-[#0F172A] dark:text-[#FAFAFA]">
                Encerrar quando o lead for ganho ou perdido no funil
              </span>
            </label>

            <div className="flex flex-col gap-2 w-full">
              <CampaignTagSelector
                label="Tags de exclusão"
                selectedTags={exclusionTags}
                onTagsChange={setExclusionTags}
              />
              <p className="text-[11px] text-[#94A3B8] dark:text-[#71717A]">
                Contatos com qualquer uma destas tags ficam pausados nesta cadência.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex flex-col gap-1 flex-1">
                <span className="text-xs text-[#64748B] dark:text-[#71717A]">Máx. de ciclos</span>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={maxCycles}
                  onChange={(e) => setMaxCycles(Number(e.target.value) || 1)}
                  className={inputClass}
                />
              </div>
              <div className="flex flex-col gap-1 flex-1">
                <span className="text-xs text-[#64748B] dark:text-[#71717A]">Espera entre ciclos (dias)</span>
                <Input
                  type="number"
                  min={0}
                  max={365}
                  value={cooldownDays}
                  onChange={(e) => setCooldownDays(Number(e.target.value) || 0)}
                  className={inputClass}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Coluna direita: etapas */}
        <div className="flex flex-col gap-4 p-5 rounded-xl border border-[#E5E7EB] bg-white shadow-sm dark:border-[#3F3F464D] dark:bg-[#27272A] dark:shadow-none">
          <div>
            <h3 className="text-base font-semibold text-[#0F172A] dark:text-[#FAFAFA]">Etapas da cadência</h3>
            <p className="text-xs text-[#64748B] dark:text-[#A1A1AA] mt-1">
              Cada etapa espera o tempo de inatividade e envia um texto ou dispara um fluxo. Se o
              cliente responder, o relógio reinicia e a cadência continua da próxima etapa.
            </p>
          </div>

          <div className="relative flex flex-col gap-3 pl-6 before:content-[''] before:absolute before:left-[9px] before:top-2 before:bottom-2 before:w-[2px] before:bg-[#E5E7EB] dark:before:bg-[#3F3F46]">
            {steps.map((step, index) => (
              <div key={step.id} className="relative">
                <span className="absolute -left-[21px] top-5 w-3 h-3 rounded-full bg-[#2563EB] border-2 border-white dark:border-[#27272A] dark:bg-[#60A5FA]" />
                <div className="flex flex-col gap-3 p-4 rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] dark:border-[#3F3F46] dark:bg-[#18181B]/40">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
                      Etapa {index + 1}
                    </p>
                    {steps.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeStep(step.id)}
                        className="p-1.5 hover:bg-red-500/15 rounded transition-colors"
                        title="Remover etapa"
                      >
                        <Trash2 size={15} className="text-red-400" />
                      </button>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <span className="flex items-center gap-1.5 text-xs text-[#64748B] dark:text-[#A1A1AA]">
                      <Clock size={13} />
                      {index === 0 ? "Após inatividade de" : "Se continuar inativo por mais"}
                    </span>
                    <Input
                      type="number"
                      min={1}
                      max={365}
                      value={step.value}
                      onChange={(e) => updateStep(step.id, { value: Math.max(1, Number(e.target.value) || 1) })}
                      className="!w-[84px] !h-[40px] rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] text-[14px] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA]"
                    />
                    <Select
                      value={step.unit}
                      onValueChange={(v) => updateStep(step.id, { unit: v as StepDraft["unit"] })}
                    >
                      <SelectTrigger className="w-[120px] !h-[40px] rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {UNIT_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Select
                      value={step.action || undefined}
                      onValueChange={(v) => updateStep(step.id, { action: v })}
                    >
                      <SelectTrigger className={selectTriggerClass}>
                        <SelectValue placeholder="O que enviar? Escolha um texto ou fluxo" />
                      </SelectTrigger>
                      <SelectContent>
                        {step.action === "ia" && (
                          <SelectGroup>
                            <SelectLabel>Configuração antiga</SelectLabel>
                            <SelectItem value="ia">Gerada com IA (legado)</SelectItem>
                          </SelectGroup>
                        )}
                        <SelectGroup>
                          <SelectLabel>Modelos de texto</SelectLabel>
                          {templates.length === 0 && (
                            <SelectItem value="t:__none" disabled>
                              Nenhum modelo cadastrado
                            </SelectItem>
                          )}
                          {templates.map((template) => (
                            <SelectItem key={template.id} value={`t:${template.id}`}>
                              {template.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel>Fluxos</SelectLabel>
                          {fluxos.length === 0 && (
                            <SelectItem value="f:__none" disabled>
                              Nenhum fluxo cadastrado
                            </SelectItem>
                          )}
                          {fluxos.map((fluxo) => (
                            <SelectItem key={fluxo.id} value={`f:${fluxo.id}`}>
                              <span className="flex items-center gap-1.5">
                                <Workflow size={13} className="text-[#2563EB] dark:text-[#60A5FA]" />
                                {fluxo.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>

                    <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 dark:border-[#3F3F464D] dark:bg-[#27272A]">
                      <p className="text-[11px] uppercase tracking-wide text-[#94A3B8] dark:text-[#71717A] mb-1">
                        Preview
                      </p>
                      <p className="text-xs text-[#475569] dark:text-[#A1A1AA] whitespace-pre-wrap break-words line-clamp-4">
                        {stepPreview(step)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={addStep}
              className="relative flex items-center justify-center gap-1.5 py-3 rounded-xl border-2 border-dashed border-[#E5E7EB] text-sm font-medium text-[#2563EB] hover:border-[#2563EB]/50 hover:bg-[#EEF4FF] transition-colors dark:border-[#3F3F46] dark:text-[#60A5FA] dark:hover:bg-blue-500/10"
            >
              <Plus size={16} />
              Adicionar etapa
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FollowUpEditor;
